/**
 * Categorías de actividad que el usuario puede activar o desactivar a la vez (filtro especial de Hermes).
 * Lógica pura, compartida por Android, web y Windows. Si no hay ninguna categoría marcada, NO se filtra nada.
 *
 * Un sitio puede encajar en varias categorías (un casino es «entretenimiento» y «ocio»; una librería es «tranquilo» y «compras»).
 * El encaje se decide con el tipo del sitio (`kind`) y, si el tipo es genérico («sport», «shop», «lugar»…), con palabras del
 * nombre y de las etiquetas («pista de skate», «ping pong», «tapas»…).
 */
import type { NearbyPlace } from "./types";

export type PlaceCategoryId = "comida" | "entretenimiento" | "tranquilo" | "historico" | "ocio" | "compras";

export type PlaceCategory = {
  id: PlaceCategoryId;
  label: string;
  icon: string;
  /** Ejemplos que se enseñan bajo el nombre. */
  hint: string;
  kinds: string[];
  /** Palabras (sin tildes, minúsculas) que, en el nombre o las etiquetas, también cuentan. */
  words: RegExp;
};

export const PLACE_CATEGORIES: PlaceCategory[] = [
  {
    id: "comida",
    label: "Comida",
    icon: "🍽️",
    hint: "Restaurantes, bares, locales de comida",
    kinds: ["restaurant", "bar", "cafe", "fast_food", "mcdonalds", "burgerking", "ice_cream", "biergarten", "bakery", "chocolate", "seafood", "tea", "pub"],
    words: /restaurante|taberna|tapas|pizzeri|hamburgues|kebab|bocadill|cerveceri|asador|marisquer|sidrer|chiringuito|cafeteri|pasteleri|brasa|trattoria|bodegon|croquet|vermut/,
  },
  {
    id: "entretenimiento",
    label: "Entretenimiento",
    icon: "🎬",
    hint: "Cine, bolera, casino, pasatiempos",
    kinds: ["cinema", "theatre", "bowling", "casino", "amusement_arcade", "escape_game", "miniature_golf", "music_venue", "circus", "theme_park", "water_park", "kart", "paintball", "zoo", "aquarium", "planetarium"],
    words: /\bcines?\b|bolera|recreativ|casino|escape|bingo|arcade|teatro|\bkarts?\b|laser|billar|ludoteca|juegos|karaoke|parque de atracciones/,
  },
  {
    id: "tranquilo",
    label: "Tranquilo",
    icon: "🌳",
    hint: "Parques, plazas, bibliotecas: calma y silencio",
    kinds: ["park", "plaza", "square", "library", "garden", "fountain", "picnic", "nature_reserve", "forest", "viewpoint", "beach", "waterfall", "spring", "dog_park", "books", "tea"],
    words: /biblioteca|jardin|jardines|parque|plaza|mirador|alameda|paseo maritimo|ribera|sala de lectura|bosque/,
  },
  {
    id: "historico",
    label: "Historia y arte",
    icon: "🏛️",
    hint: "Monumentos, castillos, museos, arte",
    kinds: ["museum", "monument", "castle", "historic", "ruins", "memorial", "archaeological", "gallery", "artwork", "church", "monastery", "mosque", "synagogue", "fort", "tower", "lighthouse", "townhall", "cemetery", "arts_centre", "exhibition", "observatory", "attraction", "film_location"],
    words: /castillo|ermita|iglesia|catedral|monasterio|museo|ruinas|torre|muralla|palacio|abadia|basilica|santuario|acueducto|yacimiento|galeria|exposicion|monumento|patrimonio/,
  },
  {
    id: "ocio",
    label: "Ocio y deporte",
    icon: "🎉",
    hint: "Discotecas, ping pong, polideportivo, fútbol, basket, skate",
    kinds: ["nightclub", "casino", "sport", "stadium", "swimming", "climbing", "ski", "bungee", "parachuting", "golf", "horse", "diving", "sailing", "fitness", "yoga", "dance", "kart", "paintball", "skate"],
    words: /ping.?pong|tenis de mesa|skate|patin|basket|baloncesto|futbol|futsal|polideportivo|pabellon|padel|pista|discoteca|\bclub\b|karaoke|billar|gimnasio|piscina|rocodromo|multiusos|campo de/,
  },
  {
    id: "compras",
    label: "Compras",
    icon: "🛍️",
    hint: "Tiendas, supermercados, bazares",
    kinds: [
      "shop", "clothes", "furniture", "hardware", "doityourself", "mall", "department_store", "supermarket", "gift", "electronics", "sports_shop",
      "jewelry", "florist", "convenience", "variety", "second_hand", "books", "marketplace", "bicycle", "toys", "pet", "alcohol", "butcher",
      "greengrocer", "cheese", "wine", "bakery",
    ],
    words: /tienda|bazar|supermercado|mercadillo|hipermercado|outlet|boutique|centro comercial|ferreteria|todo a 1|todo a cien|mercado/,
  },
];

export const CATEGORY_IDS: PlaceCategoryId[] = PLACE_CATEGORIES.map((c) => c.id);

const BY_ID = new Map(PLACE_CATEGORIES.map((c) => [c.id, c] as const));
const KIND_TO_CATEGORIES = new Map<string, PlaceCategoryId[]>();
for (const cat of PLACE_CATEGORIES) {
  for (const kind of cat.kinds) {
    KIND_TO_CATEGORIES.set(kind, [...(KIND_TO_CATEGORIES.get(kind) ?? []), cat.id]);
  }
}

export function categoryById(id: PlaceCategoryId): PlaceCategory {
  return BY_ID.get(id)!;
}

/** Texto en minúsculas y sin tildes para comparar. */
function fold(text: string): string {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Tipos genéricos: el tipo solo no basta y se mira también el nombre y las etiquetas. */
const GENERIC_KINDS = new Set(["sport", "shop", "lugar", "attraction", "historic", "variety", "convenience"]);

type PlaceLike = Pick<NearbyPlace, "kind" | "name" | "tags">;

/** Categorías a las que pertenece un sitio (puede ser ninguna: farmacia, banco, gasolinera…). */
export function categoriesOfPlace(place: PlaceLike): PlaceCategoryId[] {
  const found = new Set<PlaceCategoryId>(KIND_TO_CATEGORIES.get(place.kind) ?? []);
  if (!found.size || GENERIC_KINDS.has(place.kind)) {
    const blob = fold(`${place.name ?? ""} ${(place.tags ?? []).join(" ")}`);
    if (blob.trim()) {
      for (const cat of PLACE_CATEGORIES) {
        if (cat.words.test(blob)) found.add(cat.id);
      }
    }
  }
  return CATEGORY_IDS.filter((id) => found.has(id));
}

/** Deja solo categorías válidas, sin repetir y en el orden del catálogo (acepta cualquier cosa guardada o recibida). */
export function sanitizeCategories(raw: unknown): PlaceCategoryId[] {
  const list = Array.isArray(raw) ? raw : [];
  const set = new Set(list.map((x) => String(x)));
  return CATEGORY_IDS.filter((id) => set.has(id));
}

/** Activa o desactiva una categoría. */
export function toggleCategory(selected: PlaceCategoryId[], id: PlaceCategoryId): PlaceCategoryId[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : sanitizeCategories([...selected, id]);
}

/** Sin categorías marcadas = todo vale. */
export function placeInCategories(place: PlaceLike, selected: PlaceCategoryId[]): boolean {
  if (!selected.length) return true;
  return categoriesOfPlace(place).some((id) => selected.includes(id));
}

export function filterPlacesByCategories<T extends PlaceLike>(places: T[], selected: PlaceCategoryId[]): T[] {
  if (!selected.length) return places;
  return places.filter((p) => placeInCategories(p, selected));
}

/** «Comida, Compras» / «todas las categorías». */
export function describeCategories(selected: PlaceCategoryId[]): string {
  if (!selected.length) return "todas las categorías";
  return selected.map((id) => categoryById(id).label).join(", ");
}

/** Cuántos sitios hay de cada categoría marcada (para explicar un resultado vacío o escaso). */
export function countByCategory(places: PlaceLike[], selected: PlaceCategoryId[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of selected) out[id] = 0;
  for (const p of places) {
    for (const id of categoriesOfPlace(p)) if (id in out) out[id] += 1;
  }
  return out;
}

/**
 * Mezcla las listas de cada categoría por turnos (A, B, C, A, B, C…) para que, con varias categorías marcadas,
 * los primeros resultados no sean todos de la misma. Mantiene el orden original dentro de cada categoría.
 */
export function interleaveByCategory<T extends PlaceLike>(items: T[], selected: PlaceCategoryId[]): T[] {
  if (selected.length < 2) return items;
  const buckets = new Map<PlaceCategoryId, T[]>(selected.map((id) => [id, [] as T[]] as const));
  for (const item of items) {
    const mine = categoriesOfPlace(item).filter((id) => selected.includes(id));
    if (!mine.length) continue;
    // Si encaja en varias, va a la que tenga menos (reparto parejo).
    const target = mine.reduce((best, id) => (buckets.get(id)!.length < buckets.get(best)!.length ? id : best), mine[0]);
    buckets.get(target)!.push(item);
  }
  const out: T[] = [];
  let left = true;
  for (let round = 0; left; round++) {
    left = false;
    for (const id of selected) {
      const bucket = buckets.get(id)!;
      if (round < bucket.length) {
        out.push(bucket[round]);
        left = true;
      }
    }
  }
  return out;
}

/**
 * Plan sorpresa: elige `count` elementos al azar repartiéndolos entre las categorías marcadas
 * (con «Comida» y «Ocio» marcadas, saldrá algo de cada una). Sin categorías, es un azar normal.
 */
export function pickAcrossCategories<T extends { categorias?: string[] }>(
  items: T[],
  selected: PlaceCategoryId[],
  count: number,
  rng: () => number = Math.random,
): T[] {
  const shuffle = (list: T[]) => [...list].sort(() => rng() - 0.5);
  if (selected.length < 2) return shuffle(items).slice(0, count);
  const buckets = new Map<PlaceCategoryId, T[]>(selected.map((id) => [id, [] as T[]] as const));
  const rest: T[] = [];
  for (const item of shuffle(items)) {
    const mine = selected.filter((id) => item.categorias?.includes(id));
    if (!mine.length) {
      rest.push(item);
      continue;
    }
    const target = mine.reduce((best, id) => (buckets.get(id)!.length < buckets.get(best)!.length ? id : best), mine[0]);
    buckets.get(target)!.push(item);
  }
  const out: T[] = [];
  for (let round = 0; out.length < count; round++) {
    let any = false;
    for (const id of selected) {
      const bucket = buckets.get(id)!;
      if (round < bucket.length && out.length < count) {
        out.push(bucket[round]);
        any = true;
      }
    }
    if (!any) break;
  }
  return out.length < count ? [...out, ...rest.slice(0, count - out.length)] : out;
}
